import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconType } from 'react-icons';
import {
  FaChalkboardTeacher,
  FaBook,
  FaUserGraduate,
  FaChartBar,
  FaCog,
  FaDatabase,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';

interface AdminSidebarProps {
  className?: string;
}

// Create properly typed icon components
const TeacherIcon = FaChalkboardTeacher as IconType;
const BookIcon = FaBook as IconType;
const StudentIcon = FaUserGraduate as IconType;
const ChartIcon = FaChartBar as IconType;
const CogIcon = FaCog as IconType;
const DatabaseIcon = FaDatabase as IconType;
const ChevronDownIcon = FaChevronDown as IconType;
const ChevronRightIcon = FaChevronRight as IconType;

// Create a wrapper component for icons with proper typing
const IconComponent = ({ icon: Icon, className }: { icon: IconType; className?: string }) => {
  const IconComponent = Icon as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

const AdminSidebar: React.FC<AdminSidebarProps> = ({ className = '' }): React.ReactElement => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  const [reportsOpen, setReportsOpen] = useState(
    (currentPath.includes('/admin/reports') && !currentPath.includes('/admin/reports/subscriptions')) || 
    currentPath.includes('/admin/student-results')
  );
  const [systemOpen, setSystemOpen] = useState(
    currentPath.includes('/admin/settings') ||
    currentPath.includes('/admin/profile')
  );
  const [masterOpen, setMasterOpen] = useState(
    currentPath.includes('/admin/courses') ||
    currentPath.includes('/admin/class') ||
    currentPath.includes('/admin/stream') ||
    currentPath.includes('/admin/subject') ||
    currentPath.includes('/admin/units') ||
    currentPath.includes('/admin/chapter') ||
    currentPath.includes('/admin/topics') ||
    currentPath.includes('/admin/content') ||
    currentPath.includes('/admin/target-exams')
  );
  const [userManagementOpen, setUserManagementOpen] = useState(
    currentPath.includes('/admin/users')
  );
  const [examManagementOpen, setExamManagementOpen] = useState(
    currentPath.includes('/admin/questions') ||
    currentPath.includes('/admin/exams') ||
    currentPath.includes('/admin/offline-exam')||
    currentPath.includes('/admin/customize/exams') ||
    currentPath.includes('/admin/customize/offline-exam') ||
    currentPath.includes('/admin/entrance')
  );
  const [subscriptionManagementOpen, setSubscriptionManagementOpen] = useState(
    currentPath.includes('/admin/subscriptions') ||
    currentPath.includes('/admin/packages') ||
    currentPath.includes('/admin/package-mapping') ||
    currentPath.includes('/admin/reports/subscriptions')
  );
  const [notificationsOpen, setNotificationsOpen] = useState(
    currentPath.includes('/admin/notifications')
  );

  // Ensure Exam Management dropdown is open when on any exam management route
  useEffect(() => {
    if (
      currentPath.includes('/admin/questions') ||
      currentPath.includes('/admin/exams') ||
      currentPath.includes('/admin/offline-exam') ||
      currentPath.includes('/admin/customize/exams') ||
      currentPath.includes('/admin/customize/offline-exam') ||
      currentPath.includes('/admin/entrance')
    ) {
      setExamManagementOpen(true);
    }
  }, [currentPath]);

  useEffect(() => {
    // Keep Master menu open when navigating to any of its child routes
    if (
      currentPath.includes('/admin/courses') ||
      currentPath.includes('/admin/class') ||
      currentPath.includes('/admin/stream') ||
      currentPath.includes('/admin/subject') ||
      currentPath.includes('/admin/units') ||
      currentPath.includes('/admin/chapter') ||
      currentPath.includes('/admin/topics') ||
      currentPath.includes('/admin/content') ||
      currentPath.includes('/admin/target-exams')
      // currentPath.includes('/admin/units')
    ) {
      setMasterOpen(true);
    }
  }, [currentPath]);

  useEffect(() => {
    // Keep User Management menu open when navigating to any of its child routes
    if (currentPath.includes('/admin/users')) {
      setUserManagementOpen(true);
    }
  }, [currentPath]);

  const closeAllExcept = (keepOpen: string) => {
    if (keepOpen !== 'reports') setReportsOpen(false);
    if (keepOpen !== 'system') setSystemOpen(false);
    if (keepOpen !== 'master') setMasterOpen(false);
    if (keepOpen !== 'user') setUserManagementOpen(false);
    if (keepOpen !== 'exam') setExamManagementOpen(false);
    if (keepOpen !== 'subscription') setSubscriptionManagementOpen(false);
    if (keepOpen !== 'notifications') setNotificationsOpen(false);
  };

  const toggleReports = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('reports');
    setReportsOpen(!reportsOpen);
  };
  const toggleSystem = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('system');
    setSystemOpen(!systemOpen);
  };
  const toggleMaster = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('master');
    setMasterOpen(!masterOpen);
  };
  const toggleUserManagement = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('user');
    setUserManagementOpen(!userManagementOpen);
  };
  const toggleExamManagement = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('exam');
    setExamManagementOpen(!examManagementOpen);
  };
  const toggleSubscriptionManagement = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('subscription');
    setSubscriptionManagementOpen(!subscriptionManagementOpen);
  };
  const toggleNotifications = (e: React.MouseEvent) => {
    e.preventDefault();
    closeAllExcept('notifications');
    setNotificationsOpen(!notificationsOpen);
  };

  const isActiveLink = (path: string): boolean => {
    const adminPath = `/admin${path}`;
    // For reports section links
    if (path.startsWith('/reports/') || path === '/student-results') {
      return currentPath === adminPath;
    }
    // For master menu links
    if (path === '/courses' || path === '/content' || path === '/class' || 
        path === '/stream' || path === '/subject' || path === '/chapter' || 
        path === '/topics' || path === '/units') {
      return currentPath.startsWith(adminPath);
    }
    // For account section links
    if (path === '/settings' || path === '/profile') {
      return currentPath.startsWith(adminPath);
    }
    // For other links
    return currentPath === adminPath || currentPath.startsWith(`${adminPath}/`);
  };

  // First, let's create a reusable className for all menu items
  const menuItemClassName = (isActive: boolean): string => `
    flex items-center min-h-[40px] pl-10 pr-4 py-1.5 rounded-md text-sm font-medium
    focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111827]
    ${isActive ? 'text-white bg-[#1F2937]' : 'text-[#CBD5E1] bg-transparent hover:bg-[#1F2937] hover:text-white'}
  `.trim();

  return (
    <div className={`space-y-5 ${className || ''}`}>
      <div>
        <h3 className="text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors">
          Main
        </h3>
        <div className="mt-2 space-y-1">
          <Link 
            to="/admin/dashboard"
            className={menuItemClassName(isActiveLink('/dashboard'))}
          >
            <span className="whitespace-normal leading-tight">Dashboard</span>
          </Link>
        </div>
      </div>

      {/* User Management Dropdown */}
      <div>
        <div 
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors`}
          onClick={toggleUserManagement}
        >
          <div className="flex items-center">
            {/* <IconComponent icon={TeacherIcon} className="mr-2 h-4 w-4" /> */}
            <h3>User Management</h3>
          </div>
          <IconComponent 
            icon={userManagementOpen ? ChevronDownIcon : ChevronRightIcon} 
            className={`h-4 w-4 transform transition-transform duration-200 ${userManagementOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${userManagementOpen ? 'max-h-[400px]' : 'max-h-0'}`}>
          <Link 
            to="/admin/users"
            className={menuItemClassName(isActiveLink('/users'))}
          >
            <span className="whitespace-normal leading-tight">Users</span>
          </Link>
        </div>
      </div>

      {/* Master Menu Dropdown */}
      <div>
        <div 
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors`}
          onClick={toggleMaster}
        >
          <h3>Master Menu</h3>
          <IconComponent 
            icon={masterOpen ? ChevronDownIcon : ChevronRightIcon} 
            className={`h-4 w-4 transform transition-transform duration-200 ${masterOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${masterOpen ? 'max-h-[500px]' : 'max-h-0'}`}>
          <Link 
            to="/admin/courses"
            className={menuItemClassName(isActiveLink('/courses'))}
          >
            <span className="whitespace-normal leading-tight">Courses</span>
          </Link>
          
          <Link 
            to="/admin/class" 
            className={menuItemClassName(isActiveLink('/class'))}
          >
            <span className="whitespace-normal leading-tight">Class</span>
          </Link>

          <Link 
            to="/admin/stream" 
            className={menuItemClassName(isActiveLink('/stream'))}
          >
            <span className="whitespace-normal leading-tight">Stream</span>
          </Link>

          <Link 
            to="/admin/subject" 
            className={menuItemClassName(isActiveLink('/subject'))}
          >
            <span className="whitespace-normal leading-tight">Subjects</span>
          </Link>

          <Link 
            to="/admin/units" 
            className={menuItemClassName(isActiveLink('/units'))}
            
          >
            <span className="whitespace-normal leading-tight">Units</span>
          </Link>
          
          <Link 
            to="/admin/chapter" 
            className={menuItemClassName(isActiveLink('/chapter'))}
          >
            <span className="whitespace-normal leading-tight">Chapters</span>
          </Link>
          
          <Link
            to="/admin/topics"
            className={menuItemClassName(currentPath.includes('/admin/topics'))}
          >
            <span className="whitespace-normal leading-tight">Topics</span>
          </Link>
          
         
          
          <Link
            to="/admin/content"
            className={menuItemClassName(currentPath.includes('/admin/content'))}
          >
            <span className="whitespace-normal leading-tight">Content</span>
          </Link>
          
          <Link 
            to="/admin/target-exams"
            className={menuItemClassName(currentPath.includes('/admin/target-exams'))}
          >
            <span className="whitespace-normal leading-tight">Target Exam</span>
          </Link>
        </div>
      </div>

      {/* Exam Management Dropdown */}
      <div>
        <div 
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors`}
          onClick={toggleExamManagement}
        >
          <h3>Exam Management</h3>
          <IconComponent 
            icon={examManagementOpen ? ChevronDownIcon : ChevronRightIcon} 
            className={`h-4 w-4 transform transition-transform duration-200 ${examManagementOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${examManagementOpen ? 'max-h-[420px]' : 'max-h-0'}`}>
          <Link 
            to="/admin/questions"
            className={menuItemClassName(currentPath === '/admin/questions')}
          >
            <span className="whitespace-normal leading-tight">Questions</span>
          </Link>

          <Link 
            to="/admin/questions/import"
            className={menuItemClassName(currentPath.includes('/admin/questions/import') && !currentPath.includes('import-history'))}
          >
            <span className="whitespace-normal leading-tight">Import Questions</span>
          </Link>

          <Link
            to="/admin/questions/pending-assignment"
            className={menuItemClassName(currentPath.includes('/admin/questions/pending-assignment'))}
          >
            <span className="whitespace-normal leading-tight">Pending Assignment</span>
          </Link>

          <Link 
            to="/admin/exams"
            className={menuItemClassName(currentPath.includes('/admin/exams'))}
          >
            <span className="whitespace-normal leading-tight">Exams</span>
          </Link>

          

          {/* <Link 
            to="/admin/customize/exams"
            className={menuItemClassName(currentPath.includes('/admin/customize/exams'))}
          >
            <span className="whitespace-normal leading-tight">Customize Exam</span>
          </Link> */}

          {/* <Link 
            to="/admin/customize/offline-exam"
            className={menuItemClassName(currentPath.includes('/admin/customize/offline-exam'))}
          >
            <span className="whitespace-normal leading-tight">Customize Offline Exam</span>
          </Link> */}

          <Link 
            to="/admin/offline-exam"
            className={menuItemClassName(currentPath.includes('/admin/offline-exam'))}
          >
            <span className="whitespace-normal leading-tight">Offline Exam</span>
          </Link>

          <Link 
            to="/admin/entrance"
            className={menuItemClassName(currentPath.includes('/admin/entrance'))}
          >
            <span className="whitespace-normal leading-tight">Entrance Exams</span>
          </Link>
        </div>
      </div>

      {/* Subscription Management Dropdown */}
      <div>
        <div 
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors`}
          onClick={toggleSubscriptionManagement}
        >
          <h3>Subscription Management</h3>
          <IconComponent 
            icon={subscriptionManagementOpen ? ChevronDownIcon : ChevronRightIcon} 
            className={`h-4 w-4 transform transition-transform duration-200 ${subscriptionManagementOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${subscriptionManagementOpen ? 'max-h-[320px]' : 'max-h-0'}`}>
          <Link 
            to="/admin/subscriptions"
            className={menuItemClassName(currentPath.includes('/admin/subscriptions'))}
          >
            <span className="whitespace-normal leading-tight">Subscriptions</span>
          </Link>
          <Link
            to="/admin/packages"
            className={menuItemClassName(isActiveLink('/packages'))}
          >
            <span className="whitespace-normal leading-tight">Packages</span>
          </Link>
          <Link
            to="/admin/reports/subscriptions"
            className={menuItemClassName(isActiveLink('/reports/subscriptions'))}
          >
            <span className="whitespace-normal leading-tight">Subscriptions Reports</span>
          </Link>
        </div>
      </div>

      {/* Reports Section */}
      <div>
        <div
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors`}
          onClick={toggleReports}
        >
          <h3>Reports</h3>
          <IconComponent
            icon={reportsOpen ? ChevronDownIcon : ChevronRightIcon}
            className={`h-4 w-4 transform transition-transform duration-200 ${reportsOpen ? 'rotate-180' : ''}`}
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${reportsOpen ? 'max-h-[380px]' : 'max-h-0'}`}>
          <Link
            to="/admin/student-results"
            className={menuItemClassName(isActiveLink('/student-results'))}
          >
            <span className="whitespace-normal leading-tight">Student Results</span>
          </Link>
          <Link
            to="/admin/reports/assessments"
            className={menuItemClassName(isActiveLink('/reports/assessments'))}
          >
            <span className="whitespace-normal leading-tight">Reports of Assessments</span>
          </Link>
          <Link
            to="/admin/reports/offline-exams"
            className={menuItemClassName(isActiveLink('/reports/offline-exams'))}
          >
            <span className="whitespace-normal leading-tight">Offline Exam Reports</span>
          </Link>
          <Link
            to="/admin/reports/performance"
            className={menuItemClassName(isActiveLink('/reports/performance'))}
          >
            <span className="whitespace-normal leading-tight">Performances of Student</span>
          </Link>
        </div>
      </div>

      {/* Notifications Dropdown */}
      <div>
        <div
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors`}
          onClick={toggleNotifications}
        >
          <h3>Notifications</h3>
          <IconComponent
            icon={notificationsOpen ? ChevronDownIcon : ChevronRightIcon}
            className={`h-4 w-4 transform transition-transform duration-200 ${notificationsOpen ? 'rotate-180' : ''}`}
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${notificationsOpen ? 'max-h-[200px]' : 'max-h-0'}`}>
          <Link
            to="/admin/notifications"
            className={menuItemClassName(isActiveLink('/notifications'))}
          >
            <span className="whitespace-normal leading-tight">All Notifications</span>
          </Link>
        </div>
      </div>

      {/* Account Section */}
      {/* <div>
        <div 
          className={`flex justify-between items-center cursor-pointer text-sm font-medium text-[#CBD5E1] hover:text-white transition-colors`}
          onClick={toggleSystem}
        >
          <h3>ACCOUNT</h3>
          <IconComponent 
            icon={systemOpen ? ChevronDownIcon : ChevronRightIcon} 
            className={`h-4 w-4 transform transition-transform duration-200 ${systemOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-200 ${systemOpen ? 'max-h-40' : 'max-h-0'}`}>
          <Link 
            to="/admin/settings"
            className={menuItemClassName(isActiveLink('/settings'))}
          >
            <span className="whitespace-normal leading-tight">Settings</span>
          </Link>
          <Link 
            to="/admin/profile"
            className={menuItemClassName(isActiveLink('/profile'))}
          >
            <span className="whitespace-normal leading-tight">Profile</span>
          </Link>
        </div>
      </div> */}
    </div>
  );
};

export default AdminSidebar;

